/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import {
  customizeValidator,
  CustomValidatorOptionsType,
  Localizer,
} from '@rjsf/validator-ajv8';

import {
  _,
  ErrorObject,
} from 'ajv';
// import ajvKeywords from 'ajv-keywords';

import {
  unset,
  get,
  set,
  isEmpty,
  isObject,
  some,
  isString,
} from 'lodash';
import { RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import { formatTime, getHoursFromTime } from '../../converters';
import { validateLandline, validateMobile, validatePhone } from './phoneConstraints';

export const invalidValueErrorMessage = 'Ogiltigt värde valt';
export const requiredErrorMessage = 'Detta fält är ett krav';
export const notOnlyWhitespaceErrorMessage = 'Detta fält kan inte bestå endast av blanksteg';
export const containsAllErrorMessage = 'Valda fält måste finnas i listan';
export const oneOfErrorMessage = 'Välj ett alternativ';
export const emailError = 'Ange en giltig e-postadress';
export const nameError = 'Ange ett giltig namn';
export const passwordError = 'Lösenordet matchar inte kriterier';

// eslint-disable-next-line no-template-curly-in-string
export const nameRegex = '^(?!\\s|\\.)(?!.*[\\d#¤%&/()@£${\\[\\]}?`+´!])[^\\n]+$';
export const passwordRegex = '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d).{8,50}$';

const _errorObj = (keyword: string, field_error: string, message: string) => ({
  keyword,
  params: {
    field_error,
  },
  message,
})

export const isEmptyObject = (obj: JsonType): boolean => {
  if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
  }
  return false;
};

export const cleanObject = (obj: any, prefixes: string[]): void => {
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      cleanObject(value, prefixes);
    } else if (typeof value === 'string') {
      for (const prefix of prefixes) {
        if (value.startsWith(prefix)) {
          delete obj[key];
          break;
        }
      }
    }
  });
};

export const returnCleanObject = (obj: any, prefixes: string[]): any => {
  const clonedObj = JSON.parse(JSON.stringify(obj));
  cleanObject(clonedObj, prefixes);

  return clonedObj;
};

export const generateUUID = (): string => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  // eslint-disable-next-line no-bitwise
  const r = Math.random() * 16 | 0;
  // eslint-disable-next-line no-bitwise, no-mixed-operators
  const v = c === 'x' ? r : ((r & 0x3) | 0x8);
  return v.toString(16);
});

const _localizeCustom = (_errors?: null | ErrorObject[]): void => {
  if (!(_errors && _errors.length)) return;
  _errors.sort((a, b) => {
    const firstLetterA = a.instancePath.charAt(0).toLowerCase();
    const firstLetterB = b.instancePath.charAt(0).toLowerCase();

    if (firstLetterA < firstLetterB) return -1;
    if (firstLetterA > firstLetterB) return 1;
    return 0;
  });

  _errors.forEach((_error) => {
    const {
      keyword, params, instancePath, schemaPath, parentSchema, schema
    } = _error;
    if (!keyword) return;

    if (params?.field_error) {
        _error.schemaPath = schemaPath?.replace('fieldDependency', `properties/${params?.field_error}/${keyword}`);
        _error.instancePath = `${instancePath}/${params?.field_error}`;
      return;
    }

    if (keyword === 'uniqueItems') {
      const { errorMessage = '' } = parentSchema ?? {};
      // Object.values(_error?.params ?? {}).forEach((param: any) => {
      //   _errors.push({
      //     instancePath: `${_error.instancePath}.${param}`,
      //     message: errorMesssage[keyword] ?? _error.message,
      //     keyword: 'uniqueItems',
      //     schemaPath: _error.schemaPath,
      //     params: {},
      //   });
      // });
      _error.message = errorMessage[keyword] ?? _error.message;
      return;
    }
    if (keyword === 'oneOf') {
      _error.message = oneOfErrorMessage;
      return;
    }

    if (keyword === 'oneOf') {
      _error.message = oneOfErrorMessage;
      return;
    }

    if (keyword === 'required') {
      const isPresent = _error?.message?.includes(requiredErrorMessage);
      if (!isPresent) {
        _error.message = requiredErrorMessage;
      }
      return;
    }

    if (keyword === 'type') {
      if (_error?.parentSchema?.notEmptyNotNull !== true) {
        _error.message = '';
      }
    }

    const { errorMessage } = parentSchema || {};
    if (!errorMessage) return;
    const overrideError = errorMessage[keyword];
    if (!overrideError) return;

    _error.message = overrideError;
  });
};

export const isNumber = (value: any): boolean => typeof value === 'number';
export const sysValueFunctions = {
  getNowTime: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    const nowTime = Math.floor(now.getTime() / 1000);
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDayUnix = Math.floor(startOfDay.getTime() / 1000);

    const result = (nowTime - startOfDayUnix);
    const isValidNumber = isNumber(result);
    if (!isValidNumber) throw new Error('Failed to get sysValue. FuncName: $now_time');
    return result;
  },
  getNowDateTime: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    return Math.round(now.getTime() / 1000);
  },
  getNowDate: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDayUnix = Math.floor(startOfDay.getTime() / 1000);
    return startOfDayUnix;
  },
  getNextMidnight: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const nextMidnightUnix = Math.floor(nextMidnight.getTime() / 1000);
    return nextMidnightUnix;
  },
  getStartOfToday: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfToday: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
  getStartOfYesterday: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() - 1);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfYesterday: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() - 1);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
  getStartOfLast7Days: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() - 7);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getStartOfLast30Days: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() - 30);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getStartOfLastQuarter: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setMonth(now.getMonth() - 3);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getStartOfLastHalfYear: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setMonth(now.getMonth() - 6);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  get25MonthsAgo: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setMonth(now.getMonth() - 25);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getOneYearInFuture: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setFullYear(now.getFullYear() + 1);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },

  // future
  getStartOfTomorrow: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() + 1);
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfTomorrow: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() + 1);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfFuture7Days: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() + 7);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfFuture30Days: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setDate(now.getDate() + 30);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfFutureQuarter: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setMonth(now.getMonth() + 3);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
  getLastSecondOfFutureHalfYear: (_nowDate: Date): number => {
    const now = new Date(_nowDate);
    now.setMonth(now.getMonth() + 6);
    now.setHours(23, 59, 59, 999);
    return Math.floor(now.getTime() / 1000);
  },
};

type KeywordDefinition = {
  [key: string]: any;
};

const notEmptyNotNull: KeywordDefinition = {
  keyword: 'notEmptyNotNull',
  type: ['string', 'number'],
  validate(schema: any, data: any) {
    if (schema === false) return true;

    if (!data && data !== 0) return false;
    if (typeof data === 'string') {
      return data !== '';
    }

    return true;
  },
  error: {
    message: requiredErrorMessage,
  },
};

const objectNotEmptyNotNull: KeywordDefinition = {
  keyword: 'objectNotEmptyNotNull',
  type: ['object'],
  validate(schema: any, data: any) {
    if (!data) return false;

    const isObjectNotEmptyAndNotNull = !Object.values(data).some((value) => value == null || value === '');

    return isObjectNotEmptyAndNotNull;
  },
  errors: false,
};


const arrayMinItems: KeywordDefinition = {
  keyword: 'arrayMinItems',
  type: ['array'],
  validate(schema: any, data: any) {

    if (data) {
      return data.length >= schema;
    }

    return true;
  },
  errors: false,
};

const notOnlyWhitespace: KeywordDefinition = {
  keyword: 'notOnlyWhitespace',
  type: 'string',
  validate(schema: any, data: any) {
    // If schema is false, always return true (no validation).
    if (schema === false) return true;

    // Check if the data is a string.
    if (typeof data === 'string') {
      // Return true if the string is empty (""), but false if it contains only whitespaces.
      return data.length === 0 || data.trim().length > 0;
    }

    // Return true for non-string data to pass the validation.
    return true;
  },
  error: {
    message: notOnlyWhitespaceErrorMessage,
  },
};

const subType: KeywordDefinition = {
  keyword: 'subType',
  type: 'string',
  compile: (subTypeValue: any, fieldSchema: any) => {
    const validationTypes = {
      email: (value: string) => {
        // eslint-disable-next-line prefer-regex-literals
        const regex = new RegExp('^(?:[a-zA-Z0-9_.+-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9]{2,4})?$');

        return regex.test(value) ? true : emailError;
      },
      name: (value: string) => {
        // eslint-disable-next-line prefer-regex-literals
        const regex = new RegExp(nameRegex);
        return regex.test(value) ? true : nameError;
      },
      password: (value: string) => {
        const regex = new RegExp(passwordRegex);
        return regex.test(value) ? true : passwordError;
      },
      newPassword: (value: string) => {
        const regex = new RegExp(passwordRegex);
        return regex.test(value) ? true : 'Det nya lösenordet måste ha en längd mellan 8 och 50 tecken, innehålla både stora och små bokstäver samt minst en siffra';
      },
      phone: (value: string) => {
        const errorMessage = validatePhone(value);
        return errorMessage || true;
      },
      mobile: (value: string) => {
        const errorMessage = validateMobile(value);
        return errorMessage || true;
      },
      landline: (value: string) => {
        // eslint-disable-next-line prefer-regex-literals
        const errorMessage = validateLandline(value);
        return errorMessage || true;
      },
      county: (value: string) => {
        const { errorMessage } = fieldSchema || {};
        const errorMessageStr = errorMessage?.subType || 'Använd endast bokstäver, mellanslag och bindestreck.';

        const regex = /^(?!.*\s$)(?!.*\d)(?!.*[^\p{L}\s\-])[\p{L}]+(?:[\s-][\p{L}]+)*$/u;

        return regex.test(value) ? true : errorMessageStr;
      },
      default_values_county: (value: string) => {
        const { errorMessage } = fieldSchema || {};
        const errorMessageStr = errorMessage?.subType || 'Använd endast bokstäver, mellanslag och bindestreck.';

        if (!value) return true;
        const regex = /^(?!.*\s$)(?!.*\d)(?!.*[^\p{L}\s\-])[\p{L}]+(?:[\s-][\p{L}]+)*$/u;

        return regex.test(value) ? true : errorMessageStr;
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      default: (value: string) => true,
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return function validation(schema: string, parentSchema: any) {
      const validator = validationTypes[subTypeValue as keyof typeof validationTypes] || validationTypes.default;

      const result = validator(schema);
      if (typeof result === 'string') {
        (validation as any).errors = [{
          keyword: subTypeValue,
          params: {},
          message: result,
        }];
        return false;
      }
      return true;
    };
  },
  errors: true,
};

const minimumVendorNowEnabled: KeywordDefinition = {
  keyword: 'minimumVendorNowEnabled',
  type: 'number',
  validate(schema: any, data: any) {
    if (typeof data !== 'number') {
      return !data;
    }
    return data === -9999999 || data >= sysValueFunctions.getNowDateTime(new Date()) + 60 * 5;
  },
  // error: {
  //   message: 'Tiden måste vara minst 10 minuter framåt i tiden',
  // },
};

const maximumVendorNowEnabled: KeywordDefinition = {
  keyword: 'maximumVendorNowEnabled',
  type: 'number',
  validate(schema: any, data: any) {
    if (typeof data !== 'number') {
      return !data;
    }
    return data === -9999999 || data <= sysValueFunctions.getNowDateTime(new Date()) + (60 * 60 * 22);
  },
  // error: {
  //   message: 'Tiden får inte vara 22 timmar framåt i tiden',
  // },
};

const min: KeywordDefinition = {
  keyword: 'min',
  type: ['string', 'number'],
  validate(schema: any, data: any) {
    const numericData = parseFloat(data);
    const numericSchema = parseFloat(schema);
    if (Number.isNaN(numericData) || Number.isNaN(numericSchema)) {
      return true;
    }
    return schema > data;
  },
  errors: true,
};

const max: KeywordDefinition = {
  keyword: 'max',
  type: ['string', 'number'],
  validate(schema: any, data: any) {
    const numericData = parseFloat(data);
    const numericSchema = parseFloat(schema);
    if (Number.isNaN(numericData) || Number.isNaN(numericSchema)) {
      return true;
    }
    return schema < data;
  },
  errors: true,
};

const patternMatch: KeywordDefinition = {
  keyword: 'patternMatch',
  type: 'string',
  schemaType: 'string',
  compile(schema: any) {
    const regex = new RegExp(`^${schema}$`, 'i'); // 'i' flag for case insensitive matching
    return (data: string) => regex.test(data);
  },
  errors: true,
};

const fuzzyNotEqual: KeywordDefinition = {
  keyword: 'fuzzyNotEqual',
  type: 'string',
  compile(schemaValue: any) {
    return function validation(data: string) {
      const cleanData = data?.toLowerCase().replace(/\s+/g, ' ').trim();
      const cleanSchemaValue = schemaValue?.toLowerCase().replace(/\s+/g, ' ').trim();

      return cleanData !== cleanSchemaValue;
    };
  },
  errors: true,
};

const customArrayValidation: KeywordDefinition = {
  keyword: 'customArrayValidation',
  type: 'array',
  compile(schema: any, parentSchema: any) {
    console.log('customArrayValidation.schema', schema);
    console.log('customArrayValidation.parentSchema', parentSchema);

    // You can use `schema` and `parentSchema` to set up your validation logic

    // This is the validation function that the `compile` function returns
    return function validate(data: any[]): boolean {
      console.log('customArrayValidation:', data);

      // Here, implement your custom validation logic
      // For example, checking the length of the array
      // Return true if valid, false if not
      const isValid = true; // Placeholder for actual validation logic

      // If the data is not valid, you can use `validate.errors` to provide error messages
      // Example: validate.errors = [{ keyword: 'customArrayValidation', message: 'Validation failed' }];

      return isValid;
    };
  },
};

const _hasValue = (obj: any): boolean => {
  // Check if the value is an object or array, for recursive search
  if (isObject(obj)) {
    // Use _.some to return true if any value in the object or array satisfies the given conditions
    return some(obj, (value) => _hasValue(value));
  }
  // Conditions for considering a value as "having value"
  return !isEmpty(obj) || obj === 0 || (isString(obj) && obj !== '');
};

// const _countValues = (obj: any): number => {
//   // Check if the value is an object or array, for recursive search
//   if (isObject(obj)) {
//     // Use reduce to accumulate the count of "valuable" elements
//     return reduce(obj, (acc, value) => acc + _countValues(value), 0);
//   }
//   // Conditions for considering a value as "having value"
//   if (!isEmpty(obj) || obj === 0 || (isString(obj) && obj !== '')) {
//     return 1; // This element counts as 1
//   }
//   return 0; // This element doesn't count
// };

type Countable = { [key: string]: any } | any[];

const _countValues = (obj: Countable): number => {
  // Directly return 0 for null, as it's not countable
  if (obj === null) {
    return 0;
  }

  // Handle Arrays
  if (Array.isArray(obj)) {
    // Count each non-empty object in the array
    return obj.reduce((acc, cur) => acc + (typeof cur === 'object' && cur !== null && Object.keys(cur).length > 0 ? 1 : 0), 0);
  }

  // Handle Objects
  let count = 0;
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const element = obj[key];
      if (Array.isArray(element)) {
        // If the property is an array, count its non-empty objects
        count += _countValues(element);
      } else if (typeof element === 'object' && element !== null && Object.keys(element).length > 0) {
        // If it's a non-empty object, count it
        count += 1;
      }
    }
  }

  return count;
};

const noValues: KeywordDefinition = {
  keyword: 'noValues',
  type: 'object',
  validate(schema: any, data: any) {
    const hasValue = _hasValue(data);
    if (schema === true) {
      return hasValue === true;
    }
    return hasValue === false;
  },
  // error: {
  //   message: 'Tiden får inte vara 22 timmar framåt i tiden',
  // },
};

type FieldDependencyType = {
  validate_field: string;
  reference_field: string,
  update_field_prop?: string,
  prop?: string,
  validation: {
    type: string,
    props: {
      [key: string]: string | number,
    },
  },
  error?: {
    message: string,
    formatter: { value: string, format?: string }[]
  }
  errorMessage?: string,
};

const fieldDependency: KeywordDefinition = {
  keyword: 'fieldDependency',
  type: ['object'],
  // schemaType: ['array'],
  // eslint-disable-next-line arrow-body-style
  compile: (ruleSets: FieldDependencyType[], schema: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return function validation(formData: any, validationData: any) {
      // console.log('validationData:', validationData);
      // console.log('validationData.rootData:', validationData?.rootData);
      // TODO - migrate legacy code to use validationData?.rootData instead of replaceFormSchema and such
      // console.log("Compile", { formData, schema});
      const { properties } = schema || {};

      let validationPassed = true;
      const setValidationError = (keyword: string, field: string, message: string) => {
        validationPassed = false;
        (validation as any).errors = [{
          keyword,
          params: { field_error: field },
          message,
        }];
      };

      ruleSets?.forEach((ruleSet: FieldDependencyType) => {
        if (!validationPassed) {
          return;
        }

        const {
          validate_field,
          reference_field,
          validation: validation_rule,
          error,
        } = ruleSet ?? {};
        const { type, props } = validation_rule ?? {};
        if (validate_field && reference_field) {
          const fieldValue = formData[validate_field];
          const referenceFieldValue = formData[reference_field];

          const addModifier = typeof props?.add === 'string' ? parseInt(props?.add ?? '0', 10) : props?.add ?? 0;
          const subtractModifier = typeof props?.subtract === 'string' ? parseInt(props?.subtract ?? '0', 10) : props?.subtract ?? 0;

          const getError = (message: string, formatter: { value: string | number, format?: string }[]) => {
            if (!formatter) return message;
            let formattedMessage = message;
            formatter?.forEach((format: { value: string | number, format?: string }, index: number) => {
              const { value, format: formatType } = format;
              if (!value && value !== 0) return;
              if (formatType === 'time') {
                formattedMessage = formattedMessage.replace(`{$${index}}`, formatTime(value) ?? '');
              }

              if (formatType === 'text') {
                formattedMessage = formattedMessage.replace(`{$${index}}`, value ? `${value}` : '');
              }
            });
            return formattedMessage;
          };
          const _errorMessage = getError(error?.message ?? '', error?.formatter ?? []);

          let _referenceFieldValue;

          if (subtractModifier !== 0) {
            _referenceFieldValue = subtractModifier - (referenceFieldValue + addModifier);
          } else {
            _referenceFieldValue = referenceFieldValue + addModifier;
          }

          if (type === 'minimum') {
            if (fieldValue < _referenceFieldValue) {
              setValidationError('minimum', validate_field, _errorMessage ?? '');
            }
          }

          if (type === 'exclusiveMinimum') {
            if (fieldValue <= _referenceFieldValue) {
              setValidationError('exclusiveMinimum', validate_field, _errorMessage ?? '');
            }
          }

          if (type === 'maximum') {
            if (fieldValue > _referenceFieldValue) {
              setValidationError('maximum', validate_field, _errorMessage ?? '');
            }
          }

          if (type === 'exclusiveMaximum') {
            if (fieldValue >= _referenceFieldValue) {
              setValidationError('exclusiveMaximum', validate_field, _errorMessage ?? '');
            }
          }
        }
      });
      const newErrors = (validation as any)?.errors?.map((_error: { params: { field_error: string } }) => {
        if (!_error.params.field_error) return _error;
        return {
          ..._error,
          params: {
            ..._error.params,
            field_error: _error.params.field_error,
          },
        };
      });

      // Sets the value of a prop on a field based on the value of another field
      ruleSets?.forEach((ruleSet: FieldDependencyType) => {
        const { update_field_prop, reference_field, prop } = ruleSet ?? {};

        if (update_field_prop && reference_field && prop) {
          const fieldToUpdate = properties[update_field_prop];
          const referenceFieldValue = formData[reference_field];
          if (fieldToUpdate && fieldToUpdate?.props && referenceFieldValue) {
            fieldToUpdate.props[prop] = referenceFieldValue;
          }
        }
      });

      if ((validation as any)?.errors?.length > 0) {
        (validation as any).errors = newErrors;
      }

      return validationPassed;
    };
  },
};

const containsAll: KeywordDefinition = {
  keyword: 'contains_all',
  type: 'array',
  validate(schema: string[], data: string[]) {
    if (!Array.isArray(data) || !Array.isArray(schema)) {
      return false;
    }
    
    return data.every(item => schema.includes(item));
  },
  error: {
    message: containsAllErrorMessage,
  },
};

const generateWhiteListedKeywords = (keyValues: string[]): KeywordDefinition[] => keyValues.map((keyValue: string) => ({
  type: ['string', 'number', 'boolean', 'object', 'integer', 'null', 'array'],
  keyword: keyValue,
  validate: () => true,
}));

const fileTypes: KeywordDefinition = {
  keyword: 'fileTypes',
  type: ['array'],
  validate(acceptableFileTypes: string[], data: { base64String: string; fileName: string; }[]) {
    if (data && acceptableFileTypes) {
      for (const file of data) {
        if (!acceptableFileTypes.some((type) => file?.fileName && file.fileName.toLowerCase().endsWith(type.toLowerCase()))) {
          return false;
        }
      }
    }
    return true;
  },
  errors: true,
};

// checks if OBJECT is not equal to another object
const notEqualTo: KeywordDefinition = {
  keyword: 'notEqualTo',
  type: ['object'],
  compile(notEqualToFields: { field: string, compareTo: string }[], objectSchema: any) {
    return function validation(fieldData: any, schema: any) {
      const { rootData } = schema;
      let lastFieldWithError = '';

      const isEqual = notEqualToFields.every((rule) => {
        const { field, compareTo } = rule;

        const fieldValue = fieldData[field];
        const comparedValue = get(rootData, compareTo);

        if (!fieldValue && !comparedValue) return true;

        if (fieldValue === comparedValue) {
          lastFieldWithError = field;

          return true;
        }

        return false;
      });

      if (isEqual) {
        const { errorMessage } = objectSchema;

        const notEqualToErroMessage = errorMessage?.notEqualTo;

        (validation as any).errors = [{
          keyword: 'notEqualTo',
          params: {
            field_error: lastFieldWithError,
          },
          message: notEqualToErroMessage || 'Fälten får inte vara lika',
        }];

        return false;
      }

      return true;
    };
  },
  errors: true,
};

// checks if FIELD is not equal to another field
const notEqualToField: KeywordDefinition = {
  keyword: 'notEqualToField',
  type: ['string', 'number'],
  compile(compareToField: string) {
    return function validation(fieldData: any, schema: any) {
      const { rootData } = schema;
      const compareToFieldData = get(rootData, compareToField);

      if (fieldData) {
        return fieldData !== compareToFieldData;
      }

      return true;
    };
  },
  errors: true,
};

const equalToField: KeywordDefinition = {
  keyword: 'equalToField',
  type: ['string', 'number'],
  compile(compareToField: string) {
    return function validation(fieldData: any, schema: any) {
      const { rootData } = schema;
      const compareToFieldData = get(rootData, compareToField);

      if (fieldData) {
        return fieldData === compareToFieldData;
      }

      return true;
    };
  },
  errors: true,
};

export const isCategoryOther = (category: number, options: any[]): boolean => {
  const option = options.find((opt) => opt.id === category);
  return option?.other;
}

// const _getOverridenError = <TRootSchema extends object>(fieldName: string, rootSchema: TRootSchema) => {
//   rootSchema[]
// }


const _getErrorObject = (fieldName: string, keyword: string, rootSchema: any, fallBackMessage: string): {
  keyword: string;
  params: { field_error: string };
  message: string;
} => {
  const _val = rootSchema?.properties?.[fieldName]?.errorMessage?.[keyword]?.trim();
  return {
    keyword,
    params: {
      field_error: fieldName,
    },
    message: (!_val || _val === '') ? fallBackMessage : _val,
  };
}

const select: KeywordDefinition = {
  keyword: 'select',
  type: ['object'],
  compile(selectObj: any, rootSchema: any) {
    return function validation(fieldData: any, internalValidationPaths: any) {
      const { options, sub_category_preference_options, enable_alt } = selectObj || {};
      const {
        category, sub_category, sub_category_preference, other_category, alt_btn, alt_obj,
      } = fieldData || {};
      const categoryIds = options?.map((option: any) => option.id);
      const subCategories = options?.find((option: any) => option.id === category)?.sub_category || [];
      const subCategoriesIds = subCategories?.map((subCategory: any) => subCategory.id);
      const preferenceIds = sub_category_preference_options?.map((pref: any) => pref.id);
      const isOther = isCategoryOther(category, options);

      const errors: any[] = [];
      const sameValueErrorMessage = 'Välj annat språk eller dialekt';
      
      // Check main language object
      if (!category) {
        errors.push(_getErrorObject('category', 'required', rootSchema, requiredErrorMessage));
      }

      if (category && !categoryIds.includes(category)) {
        errors.push(_getErrorObject('category', 'invalid_value', rootSchema, invalidValueErrorMessage));
      }

      if (sub_category && !subCategoriesIds.includes(sub_category)) {
        errors.push(_getErrorObject('sub_category', 'invalid_value', rootSchema, invalidValueErrorMessage));
      }

      if (sub_category_preference && !preferenceIds.includes(sub_category_preference)) {
        errors.push(_getErrorObject('sub_category_preference', 'invalid_value', rootSchema, invalidValueErrorMessage));
      }

      if (isOther && !other_category) {
        errors.push(_getErrorObject('other_category', 'required', rootSchema, requiredErrorMessage));
      }

      // Check alt object
      if (enable_alt && alt_btn) {
        const altCategory = alt_obj?.category;
        const altSubCategory = alt_obj?.sub_category;
        const altSubCategoryPreference = alt_obj?.sub_category_preference;

        const altSubCategories = options?.find((option: any) => option.id === altCategory)?.sub_category || [];
        const altSubCategoriesIds = altSubCategories?.map((subCategory: any) => subCategory.id);
        const isAltOther = isCategoryOther(altCategory, options);

        if (!altCategory) {
          errors.push(_getErrorObject('alt_obj.category', 'required', rootSchema, requiredErrorMessage));
        } else if (!categoryIds.includes(altCategory)) {
          errors.push(_getErrorObject('alt_obj.category', 'invalid_value', rootSchema, invalidValueErrorMessage));
        }

        if (altSubCategory && !altSubCategoriesIds?.includes(altSubCategory)) {
          errors.push(_getErrorObject('alt_obj.sub_category', 'invalid_value', rootSchema, invalidValueErrorMessage));
        }
        if (altCategory === category && altSubCategory === sub_category && !isAltOther) {

          errors.push(_getErrorObject('alt_obj.sub_category', 'same_value', rootSchema, sameValueErrorMessage));
        }

        if (altSubCategoryPreference && !preferenceIds.includes(altSubCategoryPreference)) {
          errors.push(_getErrorObject('alt_obj.sub_category_preference', 'invalid_value', rootSchema, invalidValueErrorMessage));
        }

        if (isAltOther && !alt_obj.other_category) {
          errors.push(_getErrorObject('alt_obj.other_category', 'required', rootSchema, requiredErrorMessage));
        }

        if (isAltOther && alt_obj.other_category && alt_obj.other_category === other_category) {
          errors.push(_getErrorObject('alt_obj.other_category', 'same_value', rootSchema, sameValueErrorMessage)); 
        }
      }

      if (errors.length > 0) {
        (validation as any).errors = errors;
        return false;
      }

      return true;
    };
  },
  errors: true,
};

export const dateObjValidation = {
  date: {
    minimum: () => sysValueFunctions.getNowDate(new Date()),
    maximum: () => sysValueFunctions.getNowDate(new Date()) + 28944000,
  },
  start_date: {
    minimum: {
      tomorrow: () => sysValueFunctions.getNowDate(new Date()) + 86400,
    },
  },
  start_time: {
    minimum: (service_type: 'on_site' | 'video_on_site' | string, date: number, pricelist: string) => {
      const bookingNowDate = sysValueFunctions.getNowDateTime(new Date()) - date;

      if (service_type === 'on_site') {
        return bookingNowDate + 3600;
      }

      if (service_type === 'video_on_site') {
        return bookingNowDate + 3600;
      }

      return bookingNowDate + (pricelist === 'PS99' ? 1200 : 240);
    },
    // Preview (review view): lead time was already checked when entering preview,
    // so submit is allowed as long as start time is at least 1 minute ahead.
    isTooEarly: (start_time: number, service_type: string, date: number, pricelist: string, isPreview: boolean) => {
      if (isPreview) {
        return start_time < sysValueFunctions.getNowDateTime(new Date()) - date + 60;
      }
      return start_time < dateObjValidation.start_time.minimum(service_type, date, pricelist);
    },
  },
  end_time: {
    minimum: (start_time: number) => start_time + 60,
    maximum: (start_time: number) => start_time + 86100,
    warnings: (start_time: number, static_midnight_value?: number) => ({
      maximum_warning: start_time + 28800,
      midnight_warning: static_midnight_value ? static_midnight_value : 86400,
      warningMessage: {
        maximum_warning:
          'Observera att den angivna tolkningstiden är längre än åtta timmar.',
        midnight_warning:
          'Observera att tolkningstiden passerar dygnsgränsen.',
      }
    })
  }
}

const recurringDateObjectValidation = (fieldPath: string, fieldData: any, fieldSchema: any, rootData: any, validationFunction: any, isPreview = false) => {
  // console.log('fieldPath', fieldPath);
  // console.log('fieldData', fieldData);
  // console.log('fieldSchema', fieldSchema);
  // console.log('rootData', rootData);
  // console.log('validationFunction', validationFunction);
  const errors: any[] = [];
  const {
    start_date,
    start_time,
    end_time,
    end_date,
    recurring,
  } = fieldData;

  const {
    unit_type,
    unit_amount,
    days_of_week,
  } = recurring ?? {};
  const { service_type } = rootData?.base_obj || {};

  if (start_date >= end_date) {
    errors.push(_errorObj(fieldPath, 'start_date', 'Startdatum måste vara före slutdatum.'));
  }

  if (!['day', 'week'].includes(unit_type)) {
    errors.push(_errorObj(fieldPath, 'recurring.unit_type', 'Måste vara daglig eller veckovis.'));
  }

  if (unit_type === 'week' && !['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].some((day) => days_of_week?.includes(day))) {
    errors.push(_errorObj(fieldPath, 'recurring.days_of_week', 'Minst en veckodag måste väljas.'));
  }

  if (unit_amount < 1 || unit_amount > 99) {
    errors.push(_errorObj(fieldPath, 'recurring.unit_amount', 'Måste vara mellan 1 och 99.'));
  }

  const notMinToday = start_date < dateObjValidation.date.minimum();
  if (notMinToday) {
    errors.push(_errorObj(fieldPath, 'start_date', 'Startdatum måste vara minst idag.'));
  }

  const defaultErrorMessage = rootData?.base_obj?.pricelist === 'PS99'
    ? 'Eftersom bokningens starttid är inom 20 minuter hänvisar vi till tjänsten TolkNu som hittas på Språkservice Onlines startsida. Genom TolkNu kan du se om en tolk finns tillgänglig just nu.'
    : 'Eftersom starttiden är inom 4 minuter hänvisar vi till TolkNu. Där kan du se om en tolk är tillgänglig direkt.';
  const onSiteErrorMessage = rootData?.base_obj?.pricelist === 'PS99'
    ? 'Det är inte möjligt att genomföra en bokning av tolk på plats med starttid inom en timme. Välj en annan starttid.'
    : 'Det går inte att boka platstolk med starttid inom en timme. Välj en senare starttid för att fortsätta med bokningen.';
  const _serviceType = service_type === 'video' && rootData?.base_obj?.pricelist === 'PS99' ? 'default' : service_type;

  if (!notMinToday && dateObjValidation.start_time.isTooEarly(start_time, service_type, start_date, rootData?.base_obj?.pricelist, isPreview)) {
    const startTimeErrorMessage = {
      default: defaultErrorMessage,
      video: `Det går inte att boka videotolk med starttid inom 4 minuter. Välj en senare starttid för att fortsätta med bokningen.`,
      on_site: onSiteErrorMessage,
      video_on_site: onSiteErrorMessage
    }
    // @ts-ignore
    errors.push(_errorObj(fieldPath, 'start_time', startTimeErrorMessage[_serviceType] || startTimeErrorMessage.default));
  }

  if (start_date > dateObjValidation.date.maximum()) {
    errors.push(_errorObj(fieldPath, 'start_date', 'Startdatum är satt alldeles för långt fram i tiden.'));
  }

  if (end_time < dateObjValidation.end_time.minimum(start_time)) {
    errors.push(_errorObj(fieldPath, 'end_time', 'Får ej vara innan starttid.'));
  }

  if (end_time > dateObjValidation.end_time.maximum(start_time)) {
    errors.push(_errorObj(fieldPath, 'end_time', 'Får inte vara längre än 23h 55 min.'));
  }

  if (errors.length > 0) {
    (validationFunction as any).errors = errors;
    return false;
  }
  return true;
}

const serialDateObjectValidation = (fieldPath: string, fieldData: any[], fieldSchema: any, rootData: any, validationFunction: any, isPreview = false) => {
  const { serviceTypePath } = fieldSchema?.props || {};
  const serviceType = get(rootData, serviceTypePath);
  const errors: any[] = [];
  const errorMessage = 'Ogiltigt värde valt';
  const duplicateErrorMessage = 'Samma starttid och datum är inte tillåtet.';

  fieldData.forEach((dateObj: any, index: number) => {
    const { start_date_time, end_date_time } = dateObj;
    const start_date = sysValueFunctions.getNowDate(new Date(start_date_time * 1000));

    let _start_date_time_hours = getHoursFromTime(start_date_time);

    if (!start_date_time) {
      errors.push(_errorObj(fieldPath, `[${index}].start_date_time`, requiredErrorMessage));
    }

    if (!end_date_time) { errors.push(_errorObj(fieldPath, `[${index}].end_date_time`, requiredErrorMessage)); }

    if (start_date_time) {

      if (start_date < dateObjValidation.date.minimum() || start_date > dateObjValidation.date.maximum()) {
        errors.push(_errorObj(fieldPath, `[${index}].start_date`, errorMessage));
      }

      const defaultErrorMessage = rootData?.base_obj?.pricelist === 'PS99'
        ? 'Eftersom bokningens starttid är inom 20 minuter hänvisar vi till tjänsten TolkNu som hittas på Språkservice Onlines startsida. Genom TolkNu kan du se om en tolk finns tillgänglig just nu.'
        : 'Eftersom starttiden är inom 4 minuter hänvisar vi till TolkNu. Där kan du se om en tolk är tillgänglig direkt.';
      const onSiteErrorMessage = rootData?.base_obj?.pricelist === 'PS99'
        ? 'Det är inte möjligt att genomföra en bokning av tolk på plats med starttid inom en timme. Välj en annan starttid.'
        : 'Det går inte att boka platstolk med starttid inom en timme. Välj en senare starttid för att fortsätta med bokningen.';
      const _serviceType = serviceType === 'video' && rootData?.base_obj?.pricelist === 'PS99' ? 'default' : serviceType;

      if (dateObjValidation.start_time.isTooEarly(_start_date_time_hours, serviceType, start_date, rootData?.base_obj?.pricelist, isPreview)) {
        const startTimeErrorMessage = {
          default:
            defaultErrorMessage,
          video:
            `Det går inte att boka videotolk med starttid inom 4 minuter. Välj en senare starttid för att fortsätta med bokningen.`,
          on_site:
            onSiteErrorMessage,
          video_on_site:
            onSiteErrorMessage,
        };
        // @ts-ignore
        errors.push(_errorObj(fieldPath, `[${index}].start_date_time`, startTimeErrorMessage[_serviceType] || startTimeErrorMessage.default));
      }

      if (end_date_time && end_date_time < dateObjValidation.end_time.minimum(start_date_time)) {
        errors.push(_errorObj(fieldPath, `[${index}].end_date_time`, 'Får ej vara innan starttid.'));
      }

      if (end_date_time && end_date_time > dateObjValidation.end_time.maximum(start_date_time)) {
        errors.push(_errorObj(fieldPath, `[${index}].end_date_time`, 'Får inte vara längre än 23h 55 min.'));
      }
    }

    if (start_date && start_date_time) {
      const hasDuplicate = fieldData.some((otherDateObj: any, otherIndex: number) => {
        if (index !== otherIndex) {
          const other_start_date_time = otherDateObj.start_date_time;
          const other_start_date = sysValueFunctions.getNowDate(new Date(other_start_date_time * 1000));

          return start_date_time === other_start_date_time && start_date === other_start_date;
        }
        return false;
      });

      if (hasDuplicate) {
        errors.push(_errorObj(fieldPath, `[${index}].start_date_time`, duplicateErrorMessage));
      }
    }

  });

  if (errors.length > 0) {
    (validationFunction as any).errors = errors;
    return false;
  }

  return true;
};

const globalFieldSetValidation = (fieldPath: string, fieldData: any, fieldSchema: any, rootData: any, validationFunction: any) => {
  const parentKey  = fieldSchema?.props?.parentKey as string;
  const parentValue = fieldData?.[parentKey];
  const parentSchema = fieldSchema?.properties?.[parentKey];

  const dependentKey = Object.keys(fieldSchema?.properties ?? {}).filter(key => key !== 'is_toggled' && key !== parentKey)?.[0] as string;
  const dependentValue = fieldData?.[dependentKey];
  const dependentSchema = fieldSchema?.properties?.[dependentKey];

  const isToggledValue = fieldData?.is_toggled;

  if (isToggledValue) {
    validationFunction(`${fieldPath}/${parentKey}`, parentValue, parentSchema, rootData, validationFunction);
    validationFunction(`${fieldPath}/${parentKey}`, dependentValue, dependentSchema, rootData, validationFunction);
  }

  const _errors = (validationFunction as any).errors;
  if (_errors?.length > 0) { return false; }

  return true;
};

const dateObjectValidation = (fieldPath: string, fieldData: any, fieldSchema: any, rootData: any, validationFunction: any, isPreview = false) => {
  const { base_obj } = rootData || {};

  const { service_type } = base_obj || {};
  const { date, start_time, end_time } = fieldData

  const errors: any[] = [];

  if (!date) {
    errors.push(_getErrorObject('date', 'required', fieldSchema, requiredErrorMessage));
  }

  if (!start_time) {
    errors.push(_getErrorObject('start_time', 'required', fieldSchema, requiredErrorMessage));
  }

  if (!end_time) {
    errors.push(_getErrorObject('end_time', 'required', fieldSchema, requiredErrorMessage));
  }

  if (date < dateObjValidation.date.minimum() || date > dateObjValidation.date.maximum()) {
    errors.push(_getErrorObject('date', 'invalid_value', fieldSchema, invalidValueErrorMessage));
  }

  const defaultErrorMessage = base_obj?.pricelist === 'PS99'
    ? 'Eftersom bokningens starttid är inom 20 minuter hänvisar vi till tjänsten TolkNu som hittas på Språkservice Onlines startsida. Genom TolkNu kan du se om en tolk finns tillgänglig just nu.'
    : 'Eftersom starttiden är inom 4 minuter hänvisar vi till TolkNu. Där kan du se om en tolk är tillgänglig direkt.';
  const onSiteErrorMessage = base_obj?.pricelist === 'PS99'
    ? 'Det är inte möjligt att genomföra en bokning av tolk på plats med starttid inom en timme. Välj en annan starttid.'
    : 'Det går inte att boka platstolk med starttid inom en timme. Välj en senare starttid för att fortsätta med bokningen.';

  if (dateObjValidation.start_time.isTooEarly(start_time, service_type as string, date, rootData?.base_obj?.pricelist, isPreview)) {
    const _serviceTypeParsed = ((service_type === 'video' && base_obj?.pricelist !== 'PS99') || service_type === 'video_on_site' || service_type === 'on_site' ? service_type : 'default') as 'video' | 'video_on_site' | 'on_site' | 'default';
    const startTimeErrorMessage: {
      default: string;
      video: string;
      on_site: string;
      video_on_site: string;
    } = {
      default: defaultErrorMessage,
      video: `Det går inte att boka videotolk med starttid inom 4 minuter. Välj en senare starttid för att fortsätta med bokningen.`,
      on_site: onSiteErrorMessage,
      video_on_site: onSiteErrorMessage
    }
    const _serviceTypeBasedErrorMessage = startTimeErrorMessage[_serviceTypeParsed];
    errors.push(_getErrorObject('start_time', 'invalid_value', null, _serviceTypeBasedErrorMessage));
  }

  if (end_time < dateObjValidation.end_time.minimum(start_time)) {
    errors.push(_getErrorObject('end_time', 'invalid_value', fieldSchema, 'Får ej vara innan starttid.'));
  }

  if (end_time > dateObjValidation.end_time.maximum(start_time)) {
    errors.push(_getErrorObject('end_time', 'max', fieldSchema, 'Får inte vara längre än 23h 55 min.'));
  }

  if (errors.length > 0) {
    (validationFunction as any).errors = errors;
    return false;
  }

  return true;
}

const customValidationTypes: {
  [key: string]: (fieldPath: string, fieldData: any, fieldSchema: any, rootData: any, validationFunction: any, isPreview?: boolean) => boolean;
} = {
  'recurring': recurringDateObjectValidation,
  'serialDate': serialDateObjectValidation,
  'globalFieldSet': globalFieldSetValidation,
  // 'autoComplete': autoCompleteValidation,
}

// Root schema state is 'preview' in the review view (previewForm) and in backend submit validation (FormValidator).
const _isPreviewState = (it: any): boolean => it?.schemaEnv?.root?.schema?.state === 'preview';

const widgetBasedValidation: KeywordDefinition = {
  keyword: 'props',
  type: ['object', 'array'],
  compile(props: { widget?: keyof typeof customValidationTypes }, fieldSchema: any, it: any) {
    const isPreview = _isPreviewState(it);
    return function validation(fieldData: any, schemaData: any,) {
      const validateType = props.widget;
      if (!validateType || !customValidationTypes[validateType]) return true;
      const rootData = schemaData?.rootData ?? {};
      const fieldPath = schemaData?.instancePath?.replace(/\//g, '.');
      const _validationFunction = customValidationTypes[validateType];
      return _validationFunction(fieldPath, fieldData, fieldSchema, rootData, validation, isPreview);
    };
  },
  errors: true,
};

const dateObj: KeywordDefinition = {
  keyword: 'date_obj',
  type: ['object'],
  compile(keyWordValue: any, rootSchema: any, it: any) {
    const isPreview = _isPreviewState(it);
    return function validation(fieldData: any, internalValidationPaths: any) {
      const rootData = internalValidationPaths?.rootData ?? {};
      const fieldPath = internalValidationPaths?.instancePath?.replace(/\//g, '.');

      return dateObjectValidation(fieldPath, fieldData, rootSchema, rootData, validation, isPreview);
    }
  },
  errors: true,
};

const isRequiredToggled: KeywordDefinition = {
  keyword: 'is_required',
  type: ['boolean'],
  compile: (requiredFields: string[], schema: any) => {
    if (!Array.isArray(requiredFields)) return true;
    return function validation(fieldData: any, schemaData: any) {
      const parentData = schemaData?.parentData;
      if (!fieldData) return true;
      const fieldPath = schemaData?.instancePath?.replace(/\//g, '.');
      const errors: any[] = [];

      requiredFields?.forEach((fieldKey: string) => {
        const hasValue = parentData?.[fieldKey] && parentData?.[fieldKey] !== '';
        if (!hasValue) {
          const lastDotIndex = fieldPath.lastIndexOf('.');
          const pathWithoutLastPart = fieldPath.substring(0, lastDotIndex);
          const _err = {
            ..._errorObj('is_required', fieldKey, requiredErrorMessage),
            instancePath: pathWithoutLastPart,
            keyWord: 'required',
          }
          errors.push(_err);
        }
      });
      
      if (errors.length > 0) {
        (validation as any).errors = errors;
        return false;
      }
      return true;
    }
  },
  errors: true,
};

const generateCustomValidator = () => {
  const keywords: KeywordDefinition[] = [
    ...generateWhiteListedKeywords([
      'global_variables',
      'lessThan',
      'moreThan',
    ]),
    containsAll,
    isRequiredToggled,
    patternMatch,
    fuzzyNotEqual,
    notEmptyNotNull,
    objectNotEmptyNotNull,
    arrayMinItems,
    notOnlyWhitespace,
    minimumVendorNowEnabled,
    maximumVendorNowEnabled,
    subType,
    min,
    max,
    customArrayValidation,
    fieldDependency,
    fileTypes,
    notEqualTo,
    notEqualToField,
    equalToField,
    select,
    dateObj,
    noValues,
    widgetBasedValidation,
  ];

  const options: CustomValidatorOptionsType = {
    ajvOptionsOverrides: {
      // $data: true,
      allErrors: true,
      useDefaults: true,
      strict: false,
      strictNumbers: false,
      strictSchema: false,
      strictTypes: false,
      removeAdditional: false,
      // code: {
      //   regexp:
      // },
      // @ts-ignore
      keywords,
    },
  };

  const localizeCustom: Localizer = _localizeCustom;
  const validator = customizeValidator(
    options,
    localizeCustom,
  );
  // @ts-ignore
  // ajvKeywords(validator.ajv, ['']);
  return validator;
};

const withCompiledValidatorCache = <T extends ReturnType<typeof customizeValidator>>(
  v: T,
): T => {
  // @ts-ignore — AJV8Validator exposes .ajv and .localizer though they are
  // not part of the public type.
  const ajv: any = (v as any).ajv;
  if (!ajv) return v;
  // @ts-ignore
  const localizer: ((errors: unknown) => void) | undefined = (v as any).localizer;

  // Structural hash -> compiled AJV validator. Same hash means same schema
  // shape, regardless of whether the schema object was rebuilt as a new ref.
  // Bounded by the number of distinct schema shapes the form produces
  // (in practice a handful, one per resolved dependency branch).
  const compiledByHash = new Map<string, any>();
  const compiledByRootHashAndSubHash = new Map<string, any>();

  // rootSchemaRef -> synthetic ID we added it under in this Ajv instance.
  const rootIdForRoot = new WeakMap<object, string>();
  let nextRootIdSuffix = 0;

  const _isObject = (x: unknown): x is object => x !== null && typeof x === 'object';

  // Cache the structural hash directly on the schema object as a
  // non-enumerable property. This avoids re-running JSON.stringify on every
  // call: subsequent lookups for the same reference are O(1) property reads.
  // Non-enumerable means `{ ...schema }` and `JSON.stringify(schema)` do NOT
  // see this key, so it cannot leak into spreads, serialized payloads, or
  // other observers.
  const CACHE_KEY_PROP = '__schemaFormCompiledKey';
  const hashOf = (schema: object): string => {
    const stamped = (schema as any)[CACHE_KEY_PROP];
    if (typeof stamped === 'string') return stamped;
    let h: string;
    try {
      h = JSON.stringify(schema);
    } catch {
      // Cyclic or unserializable — bail to a per-call unique key so we just
      // don't cache this one.
      return '__uncacheable_' + Math.random().toString(36).slice(2);
    }
    try {
      Object.defineProperty(schema, CACHE_KEY_PROP, {
        value: h,
        enumerable: false,
        configurable: true,
        writable: true,
      });
    } catch {
      // Frozen / sealed schema. The hash is still correct; we just don't
      // get the O(1) re-lookup benefit on subsequent calls with this ref.
    }
    return h;
  };

  const ensureRootAdded = (rootSchema: object): string | null => {
    let id = rootIdForRoot.get(rootSchema);
    if (id !== undefined) return id;
    id = `__schemaFormValidatorRoot_${++nextRootIdSuffix}`;
    try {
      ajv.addSchema(rootSchema, id);
      rootIdForRoot.set(rootSchema, id);
      return id;
    } catch {
      // Likely a duplicate inner $id collision; caller falls back to uncached.
      return null;
    }
  };

  const compileForRoot = (subSchema: object, rootSchema: object): any | null => {
    const key = hashOf(rootSchema) + '::' + hashOf(subSchema);
    let compiled = compiledByRootHashAndSubHash.get(key);
    if (compiled) return compiled;
    try {
      compiled = ajv.compile(subSchema);
      compiledByRootHashAndSubHash.set(key, compiled);
      return compiled;
    } catch {
      return null;
    }
  };

  const compileStandalone = (schema: object): any | null => {
    const key = hashOf(schema);
    let compiled = compiledByHash.get(key);
    if (compiled) return compiled;
    try {
      compiled = ajv.compile(schema);
      compiledByHash.set(key, compiled);
      return compiled;
    } catch {
      return null;
    }
  };

  const origIsValid = v.isValid.bind(v);
  v.isValid = (schema, formData, rootSchema) => {
    if (!_isObject(schema) || !_isObject(rootSchema)) {
      return origIsValid(schema, formData, rootSchema);
    }
    if (ensureRootAdded(rootSchema) === null) {
      return origIsValid(schema, formData, rootSchema);
    }
    const compiled = compileForRoot(schema, rootSchema);
    if (!compiled) return origIsValid(schema, formData, rootSchema);
    try {
      return compiled(formData) as boolean;
    } catch {
      return false;
    }
  };

  const origRawValidation = v.rawValidation.bind(v);
  v.rawValidation = (schema, formData) => {
    if (!_isObject(schema)) return origRawValidation(schema, formData);
    const compiled = compileStandalone(schema);
    if (!compiled) return origRawValidation(schema, formData);
    let errors: unknown;
    try {
      compiled(formData);
      errors = compiled.errors || undefined;
      compiled.errors = null;
    } catch (e) {
      return { errors: undefined, validationError: e as Error };
    }
    if (typeof localizer === 'function' && errors) {
      localizer(errors);
    }
    return { errors: errors as any, validationError: undefined };
  };

  return v;
};

// Form IDs that use the compiled-validator cache for improved onChange performance.
// Forms not listed here use the standard (uncached) validator.
const COMPILED_CACHE_FORM_IDS = new Set<string>([
  'new_booking_content', 'new_sign_language_content'
]);

export const customValidator = generateCustomValidator();

const cachedCustomValidator = withCompiledValidatorCache(generateCustomValidator());

export const getValidatorForFormId = (formId?: string) => {
  return formId && COMPILED_CACHE_FORM_IDS.has(formId) ? cachedCustomValidator : customValidator;
};

export const updateFormData = (
  _formData: any,
  _schema: any,
) => {
  const _cleanFormData = _formData;
  const { errors = [] } = getValidatorForFormId(_schema?.global_form_id).validateFormData(_formData, _schema);
  errors?.forEach((error: RJSFValidationError): void => {
    const { name, property } = error;
    if (property !== '' && property !== undefined && !property?.startsWith('.')) return;

    if (name === 'oneOf' && property !== '' && property !== undefined) {
      unset(_cleanFormData, property.slice(1));
    }

    if (name === 'contains_all' && property !== '' && property !== undefined) {
      unset(_cleanFormData, property.slice(1));
    }
    
    // if (name === 'oneOf' && property !== '' && property !== undefined) {
    //   unset(_cleanFormData, property.slice(1));
    // }

    // if (name === 'minimum' && property !== '' && property !== undefined) {
    //   const fieldPathArray: string[] = property.split('.');
    //   const lastField: string | undefined = fieldPathArray.pop() || '';
    //   const isEndTimeType = _getField(_schema, lastField)?.props?.widget === 'endTime';
    //   if (isEndTimeType) {
    //     update(_cleanFormData, property?.slice(1), (_currentVal) => (_currentVal + 86400));
    //   }
    // }
  });

  return _cleanFormData;
};

export const validateFormDataHeadless = (
  _formRef?: any,
  _schema?: any,
  _formData?: any,
): {
  isValid: boolean;
  errorSchema: RJSFValidationError[];
} => {
  const schemaUtils = _formRef?.current?.state?.schemaUtils;
  if (!schemaUtils) return { isValid: false, errorSchema: [] };

  const resolvedSchema = schemaUtils.retrieveSchema(_schema, _formData);
  if (!resolvedSchema) return { isValid: false, errorSchema: [] };

  const result = schemaUtils
    .getValidator()
    .validateFormData(_formData, resolvedSchema);

  console.log('result', result);
  const isValid = !result?.errorSchema || Object.keys(result?.errorSchema).length === 0;

  return { isValid, errorSchema: result.errorSchema };
};

export const updateErrorsInRef = (
  _formData: any,
  _schema: any,
  _formRef: any,
  _fieldPath: string,
) => {
  if (!_fieldPath?.startsWith('root.')) return;
  const fieldPath = _fieldPath?.slice(5);
  if (!fieldPath) return;

  const _schemaValidationErrorSchema = _formRef?.current?.state?.schemaValidationErrorSchema;
  if (!_schemaValidationErrorSchema) return;

  const _schemaValidationErrors = _formRef?.current?.state?.schemaValidationErrors;
  if (!_schemaValidationErrors) return;

  const fieldHadError = get(_schemaValidationErrorSchema, fieldPath);
  if (!isEmptyObject(fieldHadError)) {
    _formRef.current.state.schemaValidationErrorSchema = null;
    _formRef.current.state.schemaValidationErrors = [];
    return;
  }

  const result = validateFormDataHeadless(_formRef, _schema, _formData);
  const _newErrorSchema = result?.isValid;
  if (result?.isValid) {
    return;
  }

  const potentialError = get(_newErrorSchema, _fieldPath);
  if (!potentialError) {
    unset(_schemaValidationErrorSchema, fieldPath);
    const newErrors = _schemaValidationErrors?.map((error: RJSFValidationError): RJSFValidationError | null => {
      const { property } = error;
      if (property !== _fieldPath) return error;

      return null;
    });
    _formRef.current.state.schemaValidationErrorSchema = _schemaValidationErrorSchema;
    _formRef.current.state.schemaValidationErrors = newErrors;
  }
};

export const cleanErrors = (_formRef: any) => {
  const _schemaValidationErrorSchema = _formRef?.current?.state?.schemaValidationErrorSchema;
  if (!_schemaValidationErrorSchema) return;

  const _schemaValidationErrors = _formRef?.current?.state?.schemaValidationErrors;
  if (!_schemaValidationErrors) return;

  _formRef.current.state.schemaValidationErrorSchema = null;
  _formRef.current.state.schemaValidationErrors = [];
};

export const getBadgeCount = (formData: any, schema: RJSFSchema): number => {
  const {
    show_badge,
  } = schema;

  if (show_badge !== true) return 0;
  return _countValues(formData);
};

export const getWarningText = (formData: any, schema: RJSFSchema) => {
  const {
    minimum_warning, maximum_warning, midnight_warning, midnight_warning_increment_hour, midnight_warning_increment_min, warningMessage,
  } = schema;
  const warningTexts: string[] = [];

  if (minimum_warning && (formData < minimum_warning)) {
    warningTexts.push(warningMessage?.minimum_warning);
  }
  if (maximum_warning && (formData > maximum_warning)) {
    warningTexts.push(warningMessage?.maximum_warning);
  }
  if (midnight_warning && (formData > midnight_warning)) {
    warningTexts.push(warningMessage?.midnight_warning);
  }


  if ((midnight_warning && (midnight_warning_increment_hour || midnight_warning_increment_min) && formData < midnight_warning)) {
    const hasPassedMidnight = (formData + midnight_warning_increment_hour + midnight_warning_increment_min) > midnight_warning;
    if (hasPassedMidnight) {
      warningTexts.push(warningMessage?.midnight_warning_increment);
    }
  }

  return warningTexts.join('\n');
};

export type JsonType = string | object | null;

export const safeParseJSON = (json: JsonType): any | null => {
  if (isEmptyObject(json)) return {};
  if (json === null || json === undefined) return {};
  if (typeof json === 'string') {
    try {
      return JSON.parse(json);
    } catch (error) {
      console.error('Failed to parse JSON (ERROR):', error);
      // console.error('Failed to parse JSON (DATA):', json);
      return null;
    }
  } else if (typeof json === 'object' && json !== null) {
    return json;
  } else {
    console.error('Invalid input:', json);
    return null;
  }
};

export const safeParseStringify = (json: JsonType): string => {
  if (typeof json === 'string') {
    const _asJson = safeParseJSON(json ?? {});
    return JSON.stringify(_asJson, null, 0);
  }

  if (typeof json === 'object' && json !== null) {
    const _asJson = safeParseJSON(json ?? {});
    return JSON.stringify(_asJson, null, 0);
  }

  return '{}';
};

type ExternalDependenciesType = {
  getExternalFormDataPath: string;
  setLocalFormDataAtPath: string;
};

export const setExternalDependenciesHeadless = (
  _localSchema: any,
  _localFormData: any,
  _externalFormData: any,
): any => {
  const _jsonSchema: any = safeParseJSON(_localSchema);
  const _externalDeps = _jsonSchema?.external_deps;
  const _newLocalFormData = _localFormData || {};

  if (_externalDeps && Array.isArray(_externalDeps)) {
    _externalDeps?.forEach((dep?: ExternalDependenciesType) => {
      if (!dep) return;
      const _valueToSet = get(_externalFormData, dep.getExternalFormDataPath);
      if (_valueToSet) {
        const _currentValue = get(_newLocalFormData, dep.setLocalFormDataAtPath);
        if (_currentValue === _valueToSet?.toString()) return;
        set(_newLocalFormData, dep.setLocalFormDataAtPath, _valueToSet.toString());
      }
    });
  }

  return _newLocalFormData;
};

export const setExternalDependencies = (
  _localSchema: any,
  _localFormData: any,
  _externalFormData: any,
): {
  formData: any;
  changedFields: string[];
} => {
  const _jsonSchema: any = safeParseJSON(_localSchema);
  const _externalDeps = _jsonSchema?.external_deps;
  const _newLocalFormData = _localFormData || {};
  const _changedFields: string[] = [];
  if (_externalDeps && Array.isArray(_externalDeps)) {
    _externalDeps?.forEach((dep?: ExternalDependenciesType) => {
      if (!dep) return;
      const _valueToSet = get(_externalFormData, dep.getExternalFormDataPath);
      if (_valueToSet) {
        const _currentValue = get(_newLocalFormData, dep.setLocalFormDataAtPath);
        if (_currentValue === _valueToSet?.toString()) return;
        _changedFields.push(`root.${dep.setLocalFormDataAtPath}`);
        set(_newLocalFormData, dep.setLocalFormDataAtPath, _valueToSet.toString());
      }
    });
  }

  return {
    formData: _newLocalFormData,
    changedFields: _changedFields,
  };
};

export type ActiveCountableFields = {
  total: number;
  tabs: {
    [key: string]: number
  };
};

type FieldCounterList = {
  [key: string]: {
    tab: string;
    root: CounterNode;
  };
};

type CounterNode = {
  list?: string[];
  [key: string]: CounterNode | string[] | undefined;
};

type OptionalFormData = {
  [key: string]: any;
};

export const getActiveCountableDiff = ({
  prev,
  next,
  schema,
}: {
  prev?: ActiveCountableFields,
  next: ActiveCountableFields,
  schema: any,
}): string[] => {
  const removedFields: string[] = [];
  if (!prev) return removedFields;

  Object.keys(prev.tabs).forEach((tabKey: string) => {
    const nextTabs = next?.tabs?.[tabKey] || 0;
    const prevTabs = prev.tabs[tabKey] || 0;
    if (prevTabs > 0 && (!next?.tabs?.[tabKey] || nextTabs <= 0)) {
      const schemaField = schema?.properties.tabOption?.oneOf?.find((field: {
        const: string,
        title: string
      }) => field.const === tabKey);
      if (schemaField) {
        removedFields.push(schemaField.title);
      }
    }
  });

  return removedFields;
};

export const getActiveCountableFields = (
  optionalFormData?: OptionalFormData,
  optionalDemandsCounterList?: FieldCounterList,
): ActiveCountableFields => {
  const activeFilters: ActiveCountableFields = {
    total: 0,
    tabs: {},
  };

  if (!optionalFormData || !optionalDemandsCounterList) return activeFilters;

  const recursiveCounter = (
    formData: any,
    counterData: CounterNode | undefined,
    tab: string,
  ) => {
    if (counterData?.list) {
      for (const field of counterData.list) {
        if (
          formData
          && formData[field]
          && (typeof formData[field] === 'boolean' || formData[field].length > 0)
        ) {
          activeFilters.total += 1;
          activeFilters.tabs[tab] = (activeFilters.tabs[tab] || 0) + 1;
        }
      }
    }
    for (const key in counterData) {
      if (key !== 'list' && typeof counterData[key] === 'object' && !Array.isArray(counterData[key])) {
        recursiveCounter(formData && formData[key], counterData[key] as CounterNode, tab);
      }
    }
  };

  for (const objKey in optionalDemandsCounterList) {
    const objCounterData = optionalDemandsCounterList[objKey];
    const objFormData = optionalFormData[objKey];
    recursiveCounter(objFormData, objCounterData?.root, objCounterData?.tab ?? '');
  }

  return activeFilters;
};

const utils = {
  getActiveCountableFields,
  getActiveCountableDiff,
  setExternalDependencies,
  setExternalDependenciesHeadless,
  updateFormData,
  validateFormDataHeadless,
  cleanErrors,
  updateErrorsInRef,
  getWarningText,
  generateUUID,
  isNumber,
  sysValueFunctions,
  isEmptyObject,
  cleanObject,
  returnCleanObject,
  generateCustomValidator,
  customValidator,
  getValidatorForFormId,
  requiredErrorMessage,
  notOnlyWhitespaceErrorMessage,
  oneOfErrorMessage,
  safeParseJSON,
  safeParseStringify,
};

export default utils;